import type { ReactNode } from "react";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  Link,
  Typography,
  useMediaQuery,
} from "@mui/material";
import type { SxProps, Theme } from "@mui/material";
import type Commande from "models/Commande";
import { dateUtil } from "@sdeapps/react-core";
import type { User } from "@sdeapps/react-core";
import ArticleList from "./ArticleList";
import statutMap from "constants/SatutMap";
import StatutChip from "components/atoms/Chips/StatutChip";
import CommandeStatutIcon from "components/molecules/cards/CommandeCard/CommandeStatutIcon";
import DisplayMagasinDeRetrait from "components/molecules/DisplayMagasinDeRetrait";
import CommandeCommentaires from "./CommandeCommentaires";

interface CommandeCardProps {
  commande: Commande;
  agent?: User | null;
  haveCardAction?: boolean;
  urlCardAction?: string;
  displayArticleListHeader?: boolean;
  displayCommentaires?: boolean;
  isLoading?: boolean;
  onClick?: () => void;
}

function CommandeCard({
  commande,
  agent,
  haveCardAction = false,
  urlCardAction,
  displayArticleListHeader = false,
  displayCommentaires = false,
  isLoading = false,
  onClick,
}: Readonly<CommandeCardProps>): ReactNode {
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const cardStyle: SxProps<Theme> = (theme: Theme) => ({
    width: "100%",
    position: "relative",
    ".MuiCardContent-root:last-child": { pb: 1 },
    ...(haveCardAction &&
      !isLoading && {
        display: "block",
        color: "inherit",
        textDecoration: "none",
        cursor: "pointer",
        "&:hover": {
          boxShadow: theme.shadows[5],
        },
      }),
  });

  return (
    <Card
      variant="outlined"
      sx={cardStyle}
      onClick={onClick}
      {...(haveCardAction &&
        !isLoading &&
        urlCardAction != null && {
          component: Link,
          href: urlCardAction,
        })}>
      <CardContent>
        <Grid container spacing={2} size={12}>
          <Grid container spacing={3} size={12} sx={{ alignItems: "center" }}>
            <Grid sx={{ height: "3Opx" }}>
              <CommandeStatutIcon statutAssociation={statutMap.get(commande.statutCommande)} />
            </Grid>
            <Grid size="grow">
              <Typography variant={isSmallScreen ? "body1" : "subtitle2"}>
                {agent?.displayName ?? commande?.demandeurNomComplet ?? "???"} -{" "}
                {agent?.employeeId ?? "???"}, du{" "}
                {dateUtil.format(commande.dateCommande, "dd/MM/yyyy")}
              </Typography>
              <StatutChip
                sx={{ background: "none", p: 0, display: "block" }}
                statut={commande.statutCommande}
              />
            </Grid>
          </Grid>
          <Grid size={12}>
            <Divider />
          </Grid>
          <Grid size={12}>
            <DisplayMagasinDeRetrait magasinDeRetrait={commande.magasinDeRetrait} />
          </Grid>
          <ArticleList articles={commande.articles} displayHeader={displayArticleListHeader} />
          {displayCommentaires && commande.commentaires.length > 0 && (
            <Grid size={12} sx={{ marginTop: -1.5 }}>
              <Divider />
            </Grid>
          )}
          {displayCommentaires && <CommandeCommentaires commentaires={commande.commentaires} />}
        </Grid>
      </CardContent>
      {isLoading && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            background: "rgba(255,255,255,0.7)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}>
          <CircularProgress size="5em" sx={{ width: "100%" }} />
        </Box>
      )}
    </Card>
  );
}

export default CommandeCard;
